import { useEffect, useRef, useCallback } from "react";

export const useParallax = (speed = 0.5) => {
  const offsetRef = useRef(0);
  const elementRef = useRef<HTMLElement | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const updateParallax = useCallback(() => {
    if (elementRef.current) {
      const newOffset = window.scrollY * speed;
      if (Math.abs(newOffset - offsetRef.current) > 0.5) {
        offsetRef.current = newOffset;
        elementRef.current.style.transform = `translate3d(0, ${newOffset}px, 0)`;
      }
    }
    rafIdRef.current = null;
  }, [speed]);

  const handleScroll = useCallback(() => {
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(updateParallax);
    }
  }, [updateParallax]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [handleScroll]);

  const setRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
    if (element) {
      element.style.willChange = "transform";
      element.style.transform = `translate3d(0, ${window.scrollY * speed}px, 0)`;
    }
  }, [speed]);

  return setRef;
};
